import { useAnalytics } from '@contexts/AnalyticsContext'
import { DatePicker, TimeRangePickerProps } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo } from 'react'

const { RangePicker } = DatePicker

const dateFormat = 'DD/MM/YYYY'

interface Props {
  rangePresets: TimeRangePickerProps['presets']
  defaultValue: [Dayjs, Dayjs]
  minDate?: Dayjs
  /** Latest pickable day; defaults to yesterday (whole days only). */
  maxDate?: Dayjs
}

const AnalyticsDate = ({
  rangePresets,
  defaultValue,
  minDate,
  maxDate,
}: Props) => {
  const { updateSelectedDates, selectedDates } = useAnalytics()

  // Adjust default value if it's before minDate
  const adjustedDefaultValue = useMemo((): [Dayjs, Dayjs] => {
    if (!minDate) return defaultValue

    const [start, end] = defaultValue
    const adjustedStart = start.isBefore(minDate, 'day') ? minDate : start
    const adjustedEnd = end.isBefore(minDate, 'day') ? minDate : end

    return [adjustedStart, adjustedEnd]
  }, [defaultValue, minDate])

  // Filter presets based on minDate
  const filteredPresets = useMemo(() => {
    if (!minDate || !rangePresets) return rangePresets

    return rangePresets.filter((preset) => {
      const [start] = preset.value as [Dayjs, Dayjs]
      return !start.isBefore(minDate, 'day')
    })
  }, [rangePresets, minDate])

  useEffect(() => {
    updateSelectedDates(adjustedDefaultValue)
  }, [])

  return (
    <RangePicker
      className="w-full"
      onChange={updateSelectedDates}
      presets={filteredPresets}
      minDate={minDate}
      maxDate={maxDate ?? dayjs().subtract(1, 'day')}
      defaultValue={adjustedDefaultValue}
      value={
        selectedDates
          ? [dayjs(selectedDates[0]), dayjs(selectedDates[1])]
          : undefined
      }
      format={dateFormat}
      allowClear={false}
    />
  )
}

type AnalyticsDateType = { Date: typeof AnalyticsDate }

export { AnalyticsDate, type AnalyticsDateType }
